import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { useApolloClient, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { GET_EQUIPMENTS } from "graphql/query/equipment-gql";
import { GET_EXHIBITIONS_CURSOR } from "graphql/query/exhibition-gql";
import { GET_FLOORING_CURSOR } from "graphql/query/flooring-gql";
import { GET_ORGANIZERS_CURSOR } from "graphql/query/organizer-gql";
import Communication from "components/communication";
import type {
  TCommunicationAuditEntry,
  TCommunicationFilters,
  TCommunicationReferenceOption,
  TCommunicationTab,
  TCommunicationTableLayout,
  TCommunicationTask,
} from "components/communication/helpers/communication-types-helper";
import Icons from "components/icons";
import { PageWrapDefault } from "components/layout";
import { DrawerStandart, Localize } from "components/service";
import { localizeByKey } from "components/service/localize";
import { useActiveMenuItem, useBreadCrumbs, useMe } from "components/use-hooks";

import "./communication.scss";

const { Search } = Input;
const basePath = Communication.Const.basePath;
const EQUIPMENT_PAGE_SIZE = 100;
const emptyFilters: TCommunicationFilters = {
  event_name: "",
  sender: "",
  company_name: "",
  stand_number: "",
};

type TCommunicationSortState = {
  key?: string;
  order?: "ascend" | "descend";
};

const TABLE_LAYOUT_BREAKPOINTS = {
  compact: 1160,
  tablet: 920,
};

const getActiveFilterCount = (filters: TCommunicationFilters): number =>
  Object.values(filters).filter((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return Boolean(value);
  }).length;

const getTabFromPath = (pathname: string): TCommunicationTab => {
  if (pathname.startsWith(`${basePath}/audit`)) {
    return "audit";
  }

  if (pathname.startsWith(`${basePath}/unidentified`)) {
    return "unidentified";
  }

  if (pathname.startsWith(`${basePath}/pending`)) {
    return "pending";
  }

  return "inbox";
};

const getPageFromPath = (pathname: string): number => {
  const pageMatch = pathname.match(/\/page\/(\d+)/);
  return pageMatch ? parseInt(pageMatch[1], 10) : 1;
};

const getRouteByTab = (tab: TCommunicationTab, page = 1): string => {
  const tabPath =
    tab === "audit"
      ? `${basePath}/audit`
      : tab === "unidentified"
        ? `${basePath}/unidentified`
        : tab === "pending"
          ? `${basePath}/pending`
          : basePath;

  return page > 1 ? `${tabPath}/page/${page}` : tabPath;
};

const mapCursorOptions = (
  edges: Array<{ node: { id: string; title: string } }> | undefined
): TCommunicationReferenceOption[] =>
  edges?.map(({ node }) => ({
    value: node.id,
    label: node.title,
  })) || [];

const sortReferenceOptions = (
  options: TCommunicationReferenceOption[]
): TCommunicationReferenceOption[] =>
  [ ...options ].sort((left, right) => left.label.localeCompare(right.label));

const mapEquipmentOptions = (
  equipments: Array<{ id: string; group_id: string; title: string }> | undefined
): TCommunicationReferenceOption[] =>
  equipments?.map((equipment) => ({
    value: equipment.group_id,
    label: equipment.title,
    rawId: equipment.id,
  })) || [];

const preventNavigation = (event: React.MouseEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const renderTabLabel = ({
  label,
  tooltip,
  count,
}: {
  label: string;
  tooltip: string;
  count?: number;
}) => (
  <span className="communication-tab-label">
    <span className="communication-tab-label__text">{label}</span>
    {typeof count === "number" && (
      <span className="communication-tab-label__badge">{count}</span>
    )}
    <Tooltip title={tooltip}>
      <span
        className="communication-tab-label__info"
        onClick={preventNavigation}
      >
        <Icons.Information />
      </span>
    </Tooltip>
  </span>
);

const matchesSearch = (task: TCommunicationTask, searchText: string): boolean => {
  const lowerSearch = searchText.toLowerCase().trim();

  if (!lowerSearch) {
    return true;
  }

  return [
    task.sender,
    task.subject,
    task.event_name,
    task.company_name,
    task.stand_number,
    task.ai_summary,
  ]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(lowerSearch));
};

const compareStrings = (left: string | null | undefined, right: string | null | undefined): number =>
  (left || "").localeCompare(right || "");

const compareDates = (left: string | null | undefined, right: string | null | undefined): number =>
  new Date(left || 0).getTime() - new Date(right || 0).getTime();

const getTableLayoutMode = (width: number): TCommunicationTableLayout => {
  if (width > 0 && width < TABLE_LAYOUT_BREAKPOINTS.tablet) {
    return "tablet";
  }

  if (width > 0 && width < TABLE_LAYOUT_BREAKPOINTS.compact) {
    return "compact";
  }

  return "full";
};

const getTableScrollWidth = (layoutMode: TCommunicationTableLayout): number => {
  if (layoutMode === "tablet") {
    return 1040;
  }

  if (layoutMode === "compact") {
    return 1140;
  }

  return 1480;
};

const sortCommunicationTasks = (
  tasks: TCommunicationTask[],
  sortState: TCommunicationSortState
): TCommunicationTask[] => {
  if (!sortState.key || !sortState.order) {
    return tasks;
  }

  const sortedTasks = [ ...tasks ];
  const direction = sortState.order === "ascend" ? 1 : -1;

  sortedTasks.sort((left, right) => {
    let compareResult = 0;

    switch (sortState.key) {
      case "sender":
        compareResult = compareStrings(left.sender, right.sender);
        break;
      case "event_name":
        compareResult = compareStrings(
          `${left.event_name} ${left.company_name}`,
          `${right.event_name} ${right.company_name}`
        );
        break;
      case "stand_number":
        compareResult = compareStrings(left.stand_number, right.stand_number);
        break;
      case "questions":
        compareResult = left.questions.length - right.questions.length;
        break;
      case "status":
        compareResult = compareStrings(left.status, right.status);
        break;
      case "received_at":
        compareResult = compareDates(left.received_at, right.received_at);
        break;
      case "approved_by":
        compareResult = compareStrings(left.approved_by, right.approved_by);
        break;
      case "approved_at":
        compareResult = compareDates(left.approved_at, right.approved_at);
        break;
      default:
        compareResult = 0;
        break;
    }

    return compareResult * direction;
  });

  return sortedTasks;
};

const CommunicationsPage: React.FC = () => {
  useActiveMenuItem([ "communication-board" ], [ "communication-board" ]);

  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { me } = useMe("cache-first");
  const canApprove = true;
  const currentTab = getTabFromPath(location.pathname);
  const currentPage = getPageFromPath(location.pathname);
  const communicationMainRef = useRef<HTMLDivElement>(null);
  const [ searchText, setSearchText ] = useState("");
  const [ filters, setFilters ] = useState<TCommunicationFilters>(emptyFilters);
  const [ equipmentOptions, setEquipmentOptions ] = useState<TCommunicationReferenceOption[]>([]);
  const [ mainContentWidth, setMainContentWidth ] = useState(0);
  const [ sortState, setSortState ] = useState<TCommunicationSortState>({
    key: "received_at",
    order: "descend",
  });
  const currentManagerName = [ me?.name, me?.surname ].filter(Boolean).join(" ").trim()
    || me?.email
    || undefined;

  const { tasks, auditEntries, saveTask, approveTask, loading, error } =
    Communication.Helpers.useCommunicationStore(filters, currentManagerName);

  useBreadCrumbs([
    {
      label: localizeByKey("BREADCRUMBS.Label_CommunicationBoard"),
      path: `${basePath}`,
    },
  ]);

  const defaultLang = me?.lang_api || "de";

  const { data: flooringData } = useQuery(GET_FLOORING_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  const { data: organizersData } = useQuery(GET_ORGANIZERS_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  const { data: exhibitionsData } = useQuery(GET_EXHIBITIONS_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    let isMounted = true;

    const loadEquipmentOptions = async () => {
      if (!defaultLang) {
        if (isMounted) {
          setEquipmentOptions([]);
        }
        return;
      }

      const optionMap = new Map<string, TCommunicationReferenceOption>();
      let page = 1;
      let lastPage = 1;

      try {
        while (page <= lastPage) {
          const { data } = await apolloClient.query({
            query: GET_EQUIPMENTS,
            variables: {
              first: EQUIPMENT_PAGE_SIZE,
              page,
              where: {
                column: "LANG",
                operator: "EQ",
                value: defaultLang,
              },
            },
            fetchPolicy: "cache-first",
          });

          mapEquipmentOptions(data?.equipments?.data).forEach((option) => {
            if (!optionMap.has(option.value)) {
              optionMap.set(option.value, option);
            }
          });

          lastPage = data?.equipments?.paginatorInfo?.lastPage || page;
          page += 1;
        }
      } finally {
        if (isMounted) {
          setEquipmentOptions(sortReferenceOptions(Array.from(optionMap.values())));
        }
      }
    };

    void loadEquipmentOptions();

    return () => {
      isMounted = false;
    };
  }, [ apolloClient, defaultLang ]);

  useEffect(() => {
    const syncMainContentWidth = () => {
      const nextWidth = Math.round(
        communicationMainRef.current?.getBoundingClientRect().width || 0
      );

      setMainContentWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    };

    syncMainContentWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncMainContentWidth);

      return () => {
        window.removeEventListener("resize", syncMainContentWidth);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      syncMainContentWidth();
    });

    if (communicationMainRef.current) {
      resizeObserver.observe(communicationMainRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const flooringOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(flooringData?.flooringsCursor?.edges),
    [ flooringData ]
  );

  const organizerOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(organizersData?.organizersCursor?.edges),
    [ organizersData ]
  );

  const exhibitionOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(exhibitionsData?.exhibitionTypesCursor?.edges),
    [ exhibitionsData ]
  );

  const summary = useMemo(
    () => Communication.Helpers.getCommunicationSummary(tasks),
    [ tasks ]
  );

  const tasksByTab = useMemo<Record<TCommunicationTab, TCommunicationTask[]>>(
    () => ({
      inbox: tasks.filter(
        (task) => task.status !== Communication.Const.statuses.APPROVED
      ),
      pending: tasks.filter(
        (task) => task.status === Communication.Const.statuses.PENDING
      ),
      unidentified: tasks.filter(
        (task) => task.status === Communication.Const.statuses.UNIDENTIFIED
      ),
      audit: tasks.filter(
        (task) => task.status === Communication.Const.statuses.APPROVED
      ),
    }),
    [ tasks ]
  );

  const visibleTasks = useMemo(
    () => tasksByTab[ currentTab ].filter((task) => matchesSearch(task, searchText)),
    [ currentTab, searchText, tasksByTab ]
  );

  const sortedTasks = useMemo(
    () => sortCommunicationTasks(visibleTasks, sortState),
    [ sortState, visibleTasks ]
  );

  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / Communication.Const.pageSize));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (safePage !== currentPage) {
      navigate(getRouteByTab(currentTab, safePage), { replace: true });
    }
  }, [ currentPage, currentTab, navigate, safePage ]);

  const paginatedTasks = useMemo(
    () =>
      sortedTasks.slice(
        (safePage - 1) * Communication.Const.pageSize,
        safePage * Communication.Const.pageSize
      ),
    [ safePage, sortedTasks ]
  );

  const auditEntryMap = useMemo<Record<string, TCommunicationAuditEntry | undefined>>(
    () =>
      auditEntries.reduce<Record<string, TCommunicationAuditEntry>>((acc, entry) => {
        if (!acc[ entry.task_id ]) {
          acc[ entry.task_id ] = entry;
        }
        return acc;
      }, {}),
    [ auditEntries ]
  );
  const tableLayoutMode = useMemo(
    () => getTableLayoutMode(mainContentWidth),
    [ mainContentWidth ]
  );
  const tableScrollWidth = useMemo(
    () => getTableScrollWidth(tableLayoutMode),
    [ tableLayoutMode ]
  );

  const rawColumns = useMemo<ColumnsType<TCommunicationTask>>(
    () =>
      Communication.Helpers.createCommunicationColumns({
        currentTab,
        layoutMode: tableLayoutMode,
        canEditRaw: false,
        canApprove,
        equipmentOptions,
        flooringOptions,
        organizerOptions,
        exhibitionOptions,
        auditEntryMap,
        onSave: (taskId, values) => saveTask(taskId, values),
        onApprove: (taskId, values) => approveTask(taskId, values),
      }),
    [
      auditEntryMap,
      currentTab,
      tableLayoutMode,
      equipmentOptions,
      exhibitionOptions,
      flooringOptions,
      organizerOptions,
      canApprove,
      approveTask,
      saveTask,
    ]
  );

  const columns = useMemo(
    () =>
      rawColumns.map((column) =>
        column.sorter
          ? {
            ...column,
            sortOrder:
              sortState.key === String(column.key)
                ? sortState.order
                : undefined,
          }
          : column
      ),
    [ rawColumns, sortState ]
  );

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [ filters ]);
  const hasActiveFilters = activeFilterCount > 0;
  const historyCount = tasksByTab.audit.length;
  const backlogCount = tasksByTab[ currentTab ].length;
  const pageNavbar = [
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationInbox"),
        count: summary.queueTotals.inbox,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Inbox"),
      }),
      path: "communication-board",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationPending"),
        count: summary.queueTotals.pending,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Pending"),
      }),
      path: "communication-board/pending",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationUnidentified"),
        count: summary.queueTotals.unidentified,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Unidentified"),
      }),
      path: "communication-board/unidentified",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationAudit"),
        count: currentTab === "audit" ? historyCount : undefined,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Audit"),
      }),
      path: "communication-board/audit",
    },
  ];

  const summaryCards = [
    {
      key: "inbox",
      label: localizeByKey("COMMUNICATION.Summary_Inbox"),
      value: summary.queueTotals.inbox,
    },
    {
      key: "unidentified",
      label: localizeByKey("COMMUNICATION.Summary_Unidentified"),
      value: summary.queueTotals.unidentified,
    },
    {
      key: "pending",
      label: localizeByKey("COMMUNICATION.Summary_Pending"),
      value: summary.queueTotals.pending,
    },
    {
      key: "parser-processed",
      label: localizeByKey("COMMUNICATION.Summary_ParserProcessed"),
      value: summary.queueTotals.parserProcessedToday,
    },
    {
      key: "manager-approved",
      label: localizeByKey("COMMUNICATION.Summary_ManagerApproved"),
      value: summary.queueTotals.managerApprovedToday,
    },
  ];

  const queueHelpKeyMap: Record<TCommunicationTab, string> = {
    inbox: "COMMUNICATION.Help_Inbox",
    pending: "COMMUNICATION.Help_Pending",
    unidentified: "COMMUNICATION.Help_Unidentified",
    audit: "COMMUNICATION.Help_Audit",
  };

  const emptyKeyMap: Record<TCommunicationTab, string> = {
    inbox: "COMMUNICATION.Empty_Inbox",
    pending: "COMMUNICATION.Empty_Pending",
    unidentified: "COMMUNICATION.Empty_Unidentified",
    audit: "COMMUNICATION.Empty_Audit",
  };

  const resultsKey =
    currentTab === "audit" ? "COMMUNICATION.Results_Audit" : "COMMUNICATION.Results_Inbox";

  return (
    <PageWrapDefault
      className="page-communication"
      dataExist={true}
      title={<Localize>PAGES.Title_CommunicationBoard</Localize>}
      pageNavbar={pageNavbar}
      staticPath=""
    >
      <div className="communication-layout">
        <div ref={communicationMainRef} className="communication-main">
          <div className="table-action-bar">
            <div className="col">
              <Search
                className="filter-search"
                prefix={<Icons.Search />}
                value={searchText}
                placeholder={localizeByKey("COMMUNICATION.Placeholder_Search")}
                onChange={(event) => setSearchText(event.target.value)}
              />

              <div className="clear-css-prop table-filter-form">
                <DrawerStandart
                  extraClass="modal-form filters"
                  modalButton={
                    <Button icon={<Icons.Filter />}>
                      <span className="ellipsis">
                        <Localize>FORMS.Button_Text_Filters</Localize>
                      </span>
                      {!!activeFilterCount && (
                        <span className="filter-count">{activeFilterCount}</span>
                      )}
                    </Button>
                  }
                >
                  <strong className="modal-title">
                    <span className="text">
                      <Localize>COMMUNICATION.Title_Filters</Localize>
                    </span>
                  </strong>

                  <Communication.Forms.Filters
                    filters={filters}
                    onApply={(values) => setFilters(values)}
                    onReset={() => setFilters(emptyFilters)}
                  />
                </DrawerStandart>

                {hasActiveFilters && (
                  <Button className="underline" onClick={() => setFilters(emptyFilters)}>
                    <span>
                      <Localize>FORMS.Button_Text_Reset</Localize>
                    </span>
                  </Button>
                )}
              </div>
            </div>

            <div className="col">
              <div className="communication-access-note communication-access-note--readonly">
                <Icons.Information />
                <span>
                  <Localize>COMMUNICATION.PreviewMode_PageNote</Localize>
                </span>
              </div>
            </div>
          </div>

          {error && (
            <Alert
              showIcon
              type="warning"
              message={error}
              style={{ marginBottom: 16 }}
            />
          )}

          <div className="communication-results-line">
            <Localize total={backlogCount} visible={sortedTasks.length}>
              {resultsKey}
            </Localize>
          </div>

          <Table<TCommunicationTask>
            rowKey="id"
            loading={loading}
            size={tableLayoutMode === "full" ? "middle" : "small"}
            className={`table-main communication-table communication-table--${tableLayoutMode}`}
            rowClassName={() => "communication-table__row"}
            tableLayout="fixed"
            columns={columns}
            dataSource={paginatedTasks}
            scroll={{ x: tableScrollWidth }}
            pagination={{
              className: "main-table-pagination",
              current: safePage,
              pageSize: Communication.Const.pageSize,
              total: sortedTasks.length,
              showSizeChanger: false,
              size: tableLayoutMode === "tablet" ? "small" : undefined,
              position: [ "bottomLeft" ],
              hideOnSinglePage: true,
            }}
            locale={{ emptyText: localizeByKey(emptyKeyMap[ currentTab ]) }}
            onChange={(pagination, _filters, sorter) => {
              const nextSorter = (Array.isArray(sorter) ? sorter[ 0 ] : sorter) as
                | SorterResult<TCommunicationTask>
                | undefined;

              setSortState({
                key: nextSorter?.columnKey ? String(nextSorter.columnKey) : undefined,
                order: nextSorter?.order || undefined,
              });

              navigate(getRouteByTab(currentTab, pagination.current || 1));
            }}
          />
        </div>

        <aside className="communication-sidebar">
          <div className="communication-help-block">
            <strong><Localize>{queueHelpKeyMap[ currentTab ]}</Localize></strong>
          </div>

          <div className="communication-summary-strip">
            {summaryCards.map((card) => (
              <div key={card.key} className="communication-summary-strip__item">
                <span className="communication-summary-strip__label">{card.label}</span>
                <strong className="communication-summary-strip__value">{card.value}</strong>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </PageWrapDefault>
  );
};

export default CommunicationsPage;
